/**
 * Browser Storage Controls — batched enforcement rollout (Jira-owned web-storage keys).
 *
 * Selectively re-enables consent enforcement for individual local-/session-storage
 * keys while the product is otherwise in analytics-only mode
 * (`disableWebStorageConsentChecks`). Each batch is gated by its own feature gate so
 * enforcement can be ramped a few keys at a time and rolled back per batch.
 *
 * The platform package consults this callback ONLY when:
 *   - `disableWebStorageConsentChecks` is true (analytics-only mode), AND
 *   - `fg('jfp-magma-selective-storage-enforcement')` is on.
 * Returning `true` re-enables the consent pipeline for that single key.
 *
 * Matching mirrors the platform categorizer precedence so the gate consulted always
 * matches the entry that decides the key's category:
 *   exact key  >  longest startsWith  >  longest endsWith  >  regexp.
 *
 * The batch definitions live in the generated `./rolloutBatches.constants.tsx` module. Once
 * `jfp-vulcan-browser-storage-enabled(-legacy)` is fully on, this callback is a no-op
 * and both modules + their gates can be deleted.
 */
import {
	CategoryStorageType,
	type ShouldEnforceKeyCallback,
} from '@atlassian/browser-storage-controls/types';
import { fg } from '@atlassian/jira-feature-gating';
import {
	CROSS_PRODUCT_ROLLOUT_BATCHES,
	KNOWN_BROWSER_EXTENSION_KEYS,
	MONOLITH_ROLLOUT_BATCHES,
	ROLLOUT_BATCHES,
	SN_ROLLOUT_BATCHES,
	type Gate,
	type KeyMatchers,
	type RolloutBatch,
} from './rolloutBatches.constants.tsx';

const LOCAL = CategoryStorageType.LocalStorage;
const SESSION = CategoryStorageType.SessionStorage;
const IDB = CategoryStorageType.IndexedDB;
type EnforceableStorageType =
	| CategoryStorageType.LocalStorage
	| CategoryStorageType.SessionStorage
	| CategoryStorageType.IndexedDB;

type Lookup = {
	exact: Map<string, Gate>;
	startsWith: Array<{ token: string; gate: Gate }>;
	endsWith: Array<{ token: string; gate: Gate }>;
	regexp: Array<{ re: RegExp; gate: Gate }>;
};

const createLookup = (): Lookup => ({ exact: new Map(), startsWith: [], endsWith: [], regexp: [] });

// Combined ramp. Drop `...SN_ROLLOUT_BATCHES` here to remove the strictly-necessary
// canary once the non-SN batches are fully rolled out. `CROSS_PRODUCT_ROLLOUT_BATCHES` covers
// `product = all` keys (their own 30-capped gates) and `MONOLITH_ROLLOUT_BATCHES` covers Jira
// monolith-owned keys; both ramp independently of the AFM team batches.
const ALL_ROLLOUT_BATCHES: readonly RolloutBatch[] = [
	...SN_ROLLOUT_BATCHES,
	...ROLLOUT_BATCHES,
	...CROSS_PRODUCT_ROLLOUT_BATCHES,
	...MONOLITH_ROLLOUT_BATCHES,
];

// Spreads one storage's column (exact/startsWith/endsWith/regexp string arrays) into the
// lookup. Extracted out of `buildLookups` so the per-kind loops aren't nested under the
// batch loop (keeps the work flat and visibly linear in the number of keys).
const indexMatchers = (lookup: Lookup, matchers: KeyMatchers, gate: Gate): void => {
	for (const value of matchers.exact ?? []) {
		lookup.exact.set(value, gate);
	}
	for (const value of matchers.startsWith ?? []) {
		lookup.startsWith.push({ token: value, gate });
	}
	for (const value of matchers.endsWith ?? []) {
		lookup.endsWith.push({ token: value, gate });
	}
	for (const value of matchers.regexp ?? []) {
		lookup.regexp.push({ re: new RegExp(value), gate });
	}
};

const buildLookups = (): Record<EnforceableStorageType, Lookup> => {
	const lookups: Record<EnforceableStorageType, Lookup> = {
		[LOCAL]: createLookup(),
		[SESSION]: createLookup(),
		[IDB]: createLookup(),
	};
	// Runs once at module load over a fixed, bounded list of batches.
	for (const batch of ALL_ROLLOUT_BATCHES) {
		if (batch.entries.local) {
			indexMatchers(lookups[LOCAL], batch.entries.local, batch.isEnabled);
		}
		if (batch.entries.session) {
			indexMatchers(lookups[SESSION], batch.entries.session, batch.isEnabled);
		}
		if (batch.entries.indexeddb) {
			indexMatchers(lookups[IDB], batch.entries.indexeddb, batch.isEnabled);
		}
	}
	// Sort token candidates by length descending so the longest-match lookup can return
	// on the first matching token instead of scanning the whole array on every call.
	for (const lookup of [lookups[LOCAL], lookups[SESSION], lookups[IDB]]) {
		lookup.startsWith.sort((a, b) => b.token.length - a.token.length);
		lookup.endsWith.sort((a, b) => b.token.length - a.token.length);
	}
	return lookups;
};

const LOOKUPS = buildLookups();

// `candidates` is pre-sorted by token length descending in `buildLookups`, so the first
// match is the longest match.
const findLongestTokenGate = (
	candidates: ReadonlyArray<{ token: string; gate: Gate }>,
	predicate: (token: string) => boolean,
): Gate | undefined => candidates.find(({ token }) => predicate(token))?.gate;

/**
 * Per-key consent-enforcement decision for the web-storage rollout. Returns `true`
 * to enforce consent for `key`, `false` to leave it in analytics-only mode.
 *
 * A key that matches a batch returns that batch's gate (on or off). Any key that matches
 * no batch — including keys not present in the cookies list — falls through to a per-
 * storage-type catch-all so unbatched keys can be enforced independently of the per-batch
 * ramp:
 *   - local/session → gated on `jfp-vulcan-browser-storage-rollout-default`
 *   - IndexedDB     → always enforced
 *
 * Keys that match `KNOWN_BROWSER_EXTENSION_KEYS` are excluded from the catch-all — they
 * are owned by third-party browser extensions, not Jira, so they must never be blocked by
 * the enforcement ramp. For the local/session catch-all the exclusion check is only
 * evaluated when the gate is already `true`, avoiding unnecessary work when the gate is off.
 *
 * Cookies are excluded by the callback type and never reach this function.
 */
export const shouldEnforceKeyForRollout: ShouldEnforceKeyCallback = (key, keyType) => {
	const lookup = LOOKUPS[keyType];
	const gate =
		lookup.exact.get(key) ??
		findLongestTokenGate(lookup.startsWith, (token) => key.startsWith(token)) ??
		findLongestTokenGate(lookup.endsWith, (token) => key.endsWith(token)) ??
		lookup.regexp.find(({ re }) => re.test(key))?.gate;
	if (gate) {
		return gate();
	}
	// Unbatched keys fall through to a per-storage-type catch-all so they can be ramped
	// (and rolled back) independently of the per-batch gates.
	if (keyType === IDB) {
		// The IndexedDB catch-all is always on, so every unbatched IndexedDB key is enforced
		// except for keys owned by known browser extensions, which must never be blocked by
		// Jira's enforcement ramp.
		return !KNOWN_BROWSER_EXTENSION_KEYS.indexeddb.includes(key);
	}
	if (!fg('jfp-vulcan-browser-storage-rollout-default')) {
		return false;
	}
	// Keys owned by known browser extensions are excluded from the catch-all gate so that
	// third-party extension storage is never inadvertently blocked by Jira's enforcement
	// ramp. Only evaluated when the catch-all gate is on.
	return !KNOWN_BROWSER_EXTENSION_KEYS.localOrSession.some((re) => re.test(key));
};
